import { Absence } from '../absences/types';
import { TimeSheetItemStatus, TimeSheetItemType } from '../schedule/types';
import { Shift } from '../shifts/types';
import {AbsenceBehavior} from '../types/types';
import {
    getTimeReportAbsenceHours,
    getTimeReportApprovedHours,
    getTimeReportBilledHours,
    getTimeReportOvertimeHours,
    getTimeReportPlannedHours,
    getTimeReportTotalHours,
    getTimeReportTimeBankBalance,
    getTimeReportWorkHours
} from '../../helpers/time-report-summary-hours';

const getReportHoursWithoutTimeBank = (hours: number, timeBankHours?: number): number => (
    Math.max(hours - Math.abs(timeBankHours ?? 0), 0)
);

export type TimeReportItem = {
    id: number;
    date: string;
    employeeName: string;
    typeName: string;
    comment: string;
    projectId: number;
    projectName: string;
    clientId: number;
    clientName: string;
    status: TimeSheetItemStatus;
    timeshiftTypeId: number;
    timeshiftType: TimeSheetItemType;
    isOvertime: boolean;
    workHours: number;
    approvedHours: number;
    billedHours: number;
    isBillable: boolean;
    absenceHours: number;
    plannedHours: number;
    overtimes: number;
    timeBankHours?: number;
    absenceBehavior?: AbsenceBehavior | null;
}

export function CreateTimeReportItemByShift(
    employeeId: number, 
    employeeName: string, 
    status: TimeSheetItemStatus, 
    shift: Shift, 
    plannedHours: number,
    clientId?: number, 
    clientName?: string): TimeReportItem {
    const resolvedClientName = clientName ?? '';
    const resolvedClientId = clientId ?? 0;
    const timeBankHours = shift.timeBankHours ?? 0;
    const reportHours = shift.hours;

    return ({
        id: employeeId,
        date: shift.date,
        employeeName: employeeName,
        typeName: shift.shiftType.name,
        comment: shift.comment,
        projectId: shift.project.id,
        projectName: shift.project.name,
        clientId: resolvedClientId,
        clientName: resolvedClientName,
        workHours: reportHours,
        status: status,
        timeshiftTypeId: shift.shiftType.id,
        timeshiftType: TimeSheetItemType.SHIFT,
        isOvertime: shift.isOvertime,
        approvedHours: status == TimeSheetItemStatus.APPROVED || status == TimeSheetItemStatus.BILLABLE ? reportHours : 0,
        billedHours: status == TimeSheetItemStatus.BILLABLE ? reportHours : 0,
        isBillable: shift.project.isBillable,
        absenceHours: 0,
        overtimes: shift.isOvertime ? reportHours : 0,
        timeBankHours: timeBankHours !== 0 ? timeBankHours : undefined,
        absenceBehavior: undefined,
        plannedHours
    });
}

export function CreateTimeReportItemByAbsence(
    employeeId: number, 
    employeeName: string, 
    status: TimeSheetItemStatus, 
    absence: Absence,
    plannedHours: number): TimeReportItem {
    const timeBankHours = absence.timeBankHours ?? 0;
    const reportHours = getReportHoursWithoutTimeBank(absence.hours, absence.timeBankHours);

    return ({
        id: employeeId,
        date: absence.date,
        employeeName: employeeName,
        typeName: absence.absenceType.name,
        comment: absence.comment,
        projectId: absence.absenceType.id,
        projectName: absence.absenceType.name,
        clientId: 0,
        clientName: '',
        workHours: 0,
        status: status,
        timeshiftTypeId: absence.absenceType.id,
        timeshiftType: TimeSheetItemType.ABSENCE,
        isOvertime: false,
        approvedHours: status == TimeSheetItemStatus.APPROVED ? reportHours : 0,
        billedHours: 0,
        isBillable: false,
        absenceHours: absence.behavior === AbsenceBehavior.WillWork ? 0 : reportHours,
        overtimes: 0,
        timeBankHours: timeBankHours !== 0 ? timeBankHours : undefined,
        absenceBehavior: absence.behavior,
        plannedHours
    });
}

export function GetGroupedWorkHours(items: TimeReportItem[]): number {
    return getTimeReportWorkHours(items)
}

export function GetGroupedAbsenceHours(items: TimeReportItem[]): number {
    return getTimeReportAbsenceHours(items)
}

export function GetTimeReportSubmittedHours(item: TimeReportItem): number {
    return item.workHours + item.absenceHours
}

export function GetGroupedTotalHours(items: TimeReportItem[]): number {
    return getTimeReportTotalHours(items)
}

export function GetGroupedApprovedHours(items: TimeReportItem[]): number {
    return getTimeReportApprovedHours(items)
}

export function GetGroupedBilledHours(items: TimeReportItem[]): number {
    return getTimeReportBilledHours(items)
}

export function GetGroupedOvertimes(items: TimeReportItem[]): number {
    return getTimeReportOvertimeHours(items)
}

export function GetGroupedPlannedHours(items: TimeReportItem[]): number {
    return getTimeReportPlannedHours(items)
}

export function GetGroupedTimeBankBalance(items: TimeReportItem[]): number | undefined {
    return getTimeReportTimeBankBalance(items);
}
